import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { createMarkup } from '@agria/utils/src/createMarkup';
import { Button, ButtonProps } from '../button/Button';
import { Typography } from '../typography/Typography';
import { Stack } from '../stack/Stack';

// Interface

export interface ButtonBarProps {
  end?: boolean;
  bothEnds?: boolean;
  centered?: boolean;
  topGap?: boolean;
  buttonOne?: ButtonProps;
  buttonTwo?: ButtonProps;
  additionalTextPre?: string | React.ReactNode;
  additionalText?: string | React.ReactNode;
  additionalTextTwo?: string | React.ReactNode;
  heading?: string | React.ReactNode;
}

export interface StyledButtonBarProps {
  $centered?: boolean;
  $end?: boolean;
  $topGap?: boolean;
  $bothEnds?: boolean;
}

// Styles

export const StyledButtonBar = styled.div<StyledButtonBarProps>`
  ${({
    theme: { space, maxMedia, media },
    $centered,
    $end,
    $topGap,
    $bothEnds,
  }) => css`
    display: flex;
    gap: ${space.small};
    flex-direction: column;

    ${$topGap &&
    css`
      margin-top: ${space.fluidXLarge};
    `};

    @media (max-width: ${maxMedia.medium}) {
      flex-direction: column;

      > * {
        width: 100%;
      }
    }

    @media (min-width: ${media.medium}) {
      flex-direction: row;

      ${$centered &&
      css`
        justify-content: center;
      `};
      ${$end &&
      css`
        justify-content: flex-end;
      `};
      ${$bothEnds &&
      css`
        flex-direction: row-reverse;
        justify-content: space-between;
      `};
    }
  `};
`;

const StyledDiscount = styled(Typography)`
  ${({ theme: { space } }) => css`
    display: flex;
    gap: ${space.xxxSmall};
    align-items: center;
    justify-content: center;
    text-align: center;
    height: 2rem;
  `};
`;

// JSX

export const ButtonBar: FC<ButtonBarProps> = ({
  buttonOne,
  buttonTwo,
  centered,
  topGap,
  bothEnds,
  end,
  additionalTextPre,
  additionalText,
  additionalTextTwo,
  heading,
}) => (
  <Stack gap="fluidMedium">
    {heading && (
      <StyledDiscount component="p" variant="headingMedium">
        {heading}
      </StyledDiscount>
    )}
    {additionalTextPre && (
      <StyledDiscount component="p" variant="bodyMediumBold">
        {additionalTextPre}
      </StyledDiscount>
    )}
    <StyledButtonBar
      $centered={centered}
      $end={end}
      $topGap={topGap}
      $bothEnds={bothEnds}
    >
      {buttonOne && (
        <Button
          variant="primary"
          arrowRight
          {...buttonOne}
          data-test={buttonOne.dataTest}
        />
      )}
      {buttonTwo && (
        <Button
          variant="secondary"
          {...buttonTwo}
          data-test={buttonTwo.dataTest}
        />
      )}
    </StyledButtonBar>
    <Stack gap="xxSmall">
      {additionalText && (
        <StyledDiscount component="p" variant="bodyMediumBold">
          {additionalText}
        </StyledDiscount>
      )}
      {additionalTextTwo && (
        <StyledDiscount component="p" variant="bodySmall">
          {createMarkup(additionalTextTwo)}
        </StyledDiscount>
      )}
    </Stack>
  </Stack>
);

export default ButtonBar;
